import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    Typography,
    Grid,
    IconButton,
    Card,
    CardContent,
    Toolbar, Tooltip,
    TextField,
    Pagination,
    FormControlLabel,
    Switch,
    Stack, Divider,
    Dialog, DialogTitle, DialogContent, DialogActions,
    useTheme, useMediaQuery
} from "@mui/material";
import {
    DataGrid,
    GridColDef,
    GridRowModes,
    GridRowModesModel,
    GridActionsCellItem,
    GridRowId,
    GridRowEditStopReasons
} from "@mui/x-data-grid";
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Save as SaveIcon, Close as CancelIcon, AddCard as AddCardIcon } from "@mui/icons-material";

import { Payment, PaymentLinesProps, PaymentLineDocumentResultModel } from "../../Types/SalesDocument/PaymentType";
import { ListQuery, Option } from "../../Types/CommonTypes";
import { useAuth, useSnackbar, useLoading } from "../../Context/Contexts";
import { PaymentMethodAutoComplete, PaymentCertificateCodeAutoComplete } from "../../components/AutoComplete/AutoCompletes";
import PayByLink from "./PayByLink";
import { useTranslation } from "react-i18next";
const PaymentLines: React.FC<PaymentLinesProps & { setPaymentsOnDocument: React.Dispatch<React.SetStateAction<Payment[]>> }> = ({ documentNo, documentType, readUrl, updateUrl, deleteUrl, xMobileNo, initialPayments, setPaymentsOnDocument, history = false, locked }) => {
    const { authService } = useAuth();
    const { showSnackbar } = useSnackbar();
    const { t } = useTranslation();
    const [payments, setPayments] = useState<Payment[]>(initialPayments ?? []);
    const [totalCount, setTotalCount] = useState(0);
    const [page, setPage] = useState(0);
    const { showLoading, hideLoading } = useLoading();
    const [isLoading, setIsLoading] = useState(false);
    const [query, setQuery] = useState<ListQuery>({ page: 0, pageSize: 100, filter: "" });
    const [pageSize, setPageSize] = useState(10);
    const [loading, setLoading] = useState(false);
    const [editRow, setEditingRow] = useState<Payment | null>(null);
    const [paybyLinkModal, setPaybyLinkModal] = useState(false);
    const [showCertificates, setShowCertificates] = useState(false);
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
    // Fetch payments from the server

    useEffect(() => {
        setQuery((prev) => ({
            ...prev,
            Page: page,
            PageSize: pageSize,
        }));
    }, [page, pageSize]);

    //useEffect(() => {
    //    const loadPaymentMethods = async () => {
    //        const options = await fetchPaymentOptions("", 0); // Load initial set
    //        setPaymentMethods(options);
    //    };

    //    loadPaymentMethods();
    //}, []);

    useEffect(() => {
        if (isLoading) showLoading();
        else hideLoading();
    }, [isLoading, showLoading, hideLoading]);

    //useEffect(() => {
    //    console.log("setPaymentsOnDocument");
    //    setPaymentsOnDocument(payments);
    //}, [payments, setPaymentsOnDocument]);


    //const fetchPaymentOptions = async (search: string, offset: number, code?: string): Promise<Option[]> => {
    //    setIsLoading(true);
    //    const url = code
    //        ? `/autocompleteApi/GetPaymentOptions?code=${code}`
    //        : `/autocompleteApi/GetPaymentOptions?search=${search}&offset=${offset}&limit=50`;

    //    try {
    //        const response = await authService.get<Option[]>(url);
    //        return response || [];
    //    } catch (error) {
    //        console.error("Failed to fetch payment options:", error);
    //        return [];
    //    }
    //    finally {
    //        setIsLoading(false);
    //    }
    //};

    //const fetchCertificateOptions = async (search: string, offset: number, code?: string): Promise<Option[]> => {
    //    setIsLoading(true);
    //    const url = code
    //        ? `/autocompleteApi/GetCertificateOptions?code=${code}`
    //        : `/autocompleteApi/GetCertificateOptions?search=${search}&offset=${offset}&limit=50`;

    //    try {
    //        const response = await authService.get<Option[]>(url);
    //        return response || [];
    //    } catch (error) {
    //        console.error("Failed to fetch certificate options:", error);
    //        return [];
    //    }
    //    finally {
    //        setIsLoading(false);
    //    }
    //};


    const setPaymentsOnPayment = (updateFn: (prevPayments: Payment[] | null) => Payment[]) => {
        setPayments(updateFn(payments ?? []));
    };

    const wrappedSetPayments = (newPayments: Payment[] | ((prevPayments: Payment[]) => Payment[])) => {
        setPaymentsOnPayment((prevPayments) =>
            typeof newPayments === "function" ? newPayments(prevPayments ?? []) : newPayments
        );
        setPaymentsOnDocument(newPayments);
    };

    const handlePayByLink = () => {
        setPaybyLinkModal(true);
    };

    const handleAddPayment = async () => {
        const newPayment: Payment = {
            documentNo,
            documentType,
            createNew : true,
        };

        setEditingRow(newPayment);
    };

    const handleEditPayment = async (payment: Payment) => {
        setEditingRow(payment);
    };

    const handleSavePayment = async () => {
        if (!editRow) return;

        try {
            setIsLoading(true);
            const response = await authService.post<Payment>(updateUrl, editRow);

            setPayments((prev) => {
                const updated = prev.some(p => p.id === editRow.id)
                    ? prev.map(p => p.id === editRow.id ? response : p)
                    : [response, ...prev];
                setPaymentsOnDocument(updated);
                return updated;
            });

            showSnackbar(t("dialog.paymentSaved"), "success");
            setEditingRow(null);
        } catch (error: any) {
            showSnackbar(`${t("dialog.failedToSaveChanges")} : ${error.response?.data ?? error.message}`, "error");
        } finally {
            setIsLoading(false);
        }
    };



    const handleDeletePayment = async (payment: Payment) => {
        const confirmed = window.confirm(t("dialog.confirmDeletePayment"));
        if (!confirmed) return;

        try {
            setIsLoading(true);
            await authService.post(deleteUrl, payment);

            setPayments((prev) => {
                const updated = prev.filter(p => p.id !== payment.id);
                setPaymentsOnDocument(updated);
                return updated;
            });

            showSnackbar(t("dialog.paymentDeleted"), "success");
        } catch (error: any) {
            showSnackbar(`${t("dialog.paymentDeleteError")} : ${error.response?.data ?? error.message}`, "error");
        } finally {
            setIsLoading(false);
        }
    };

    const handleFieldChange = (field: keyof Payment, value: any) => {
        setEditingRow((prev) => {
            if (!prev) return null;
            return { ...prev, [field]: value };
        });
    };


    return (
        <Box sx={{ padding: 2 }}>
            <Typography variant="h4" sx={{ mb: 2, textAlign: "center" }}>
                {t("common.payments")}
            </Typography>
            {!history && locked !== true && (
                <>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddPayment} sx={{ mb: 2 }} fullWidth>
                        {t("actions.addPayment")}
                    </Button>
                    <Button variant="contained" startIcon={<AddCardIcon />} onClick={handlePayByLink} sx={{ mb: 2 }} fullWidth>
                        {t("actions.payByLink")}
                    </Button>
                </>
            )}
            <Grid container spacing={2}>
                {payments.map((payment, index) => (
                    <Grid item xs={12} key={payment.id}>
                        <Grid
                            container
                            spacing={1}
                            sx={{
                                backgroundColor: index % 2 === 0 ? "transparent" : "#fafafa",
                                ":hover": {
                                    backgroundColor: index % 2 === 0 ? "#f0f0f0" : "#e8e8e8",
                                },
                                alignItems: "center",
                            }}
                        >
                            {/* Left: method & certificate */}
                            <Grid item xs={12} lg={6}>
                                <Typography variant="subtitle2" color="text.secondary">
                                    {t("common.paymentMethod")}
                                </Typography>
                                <Typography variant="body1">{payment.paymentMethodDescription}</Typography>
                                {payment.certificateCode && (
                                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                                        {t("common.certificateCode")}: {payment.certificateCode}
                                    </Typography>
                                )}
                            </Grid>

                            {/* Middle: amount */}
                            <Grid item xs={12} lg={4} sx={{ flex: "0 0 auto", pr: 1 }}>
                                <Typography variant="subtitle2" color="text.secondary">
                                    {t("common.amount")}
                                </Typography>
                                <Typography variant="body1">
                                    {payment.amount} {payment.currencyCode}
                                </Typography>
                            </Grid>

                            {/* Right: actions */}
                            {locked !== true && !history && (
                                <>
                                    <Grid xs={12} lg={1} item sx={{ display: "flex", justifyContent: "flex-end" }}>
                                        <Tooltip title={t("common.edit")}>
                                            <IconButton
                                                onClick={() => handleEditPayment(payment)}
                                                size="small"
                                                color="secondary"
                                            >
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Grid>
                                    <Grid xs={12} lg={1} item sx={{ display: "flex", justifyContent: "flex-end" }}>
                                        <Tooltip title={t("common.delete")}>
                                            <IconButton
                                                onClick={() => handleDeletePayment(payment)}
                                                size="small"
                                                color="error"
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Grid>

                                </>
                            )}
                        </Grid>
                    </Grid>
                ))}

                {!isLoading && payments.length === 0 && (
                    <Grid item xs={12}>
                        <Typography align="center">{t("common.noEntriesFound")}</Typography>
                    </Grid>
                )}


            </Grid>
            <Dialog
                open={!!editRow}
                onClose={() => setEditingRow(null)}
                fullWidth
                maxWidth="sm"
              
            >
                <DialogTitle>
                    {editRow?.createNew ? t("common.createPayment") : t("common.editPayment")}
                </DialogTitle>

                <DialogContent dividers>
                    <PaymentMethodAutoComplete
                        valueId={editRow?.paymentMethodId ?? null}
                        load
                        onChange={(val) => handleFieldChange("paymentMethodId", val?.value)}
                    />

                    <TextField
                        fullWidth
                        margin="normal"
                        type="number"
                        label={t("common.amount")}
                        value={editRow?.amount ?? ""}
                        onChange={(e) => handleFieldChange("amount", +e.target.value)}
                    />

                    <Grid item xs={12} sx={{ mt: 1 }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={showCertificates}
                                    onChange={(e) => setShowCertificates(e.target.checked)}
                                />
                            }
                            label={t("common.showCertificates")}
                        />
                    </Grid>

                    {showCertificates && (
                        <PaymentCertificateCodeAutoComplete
                            valueId={editRow?.certificateCode ?? null}
                            load
                            onChange={(val) => handleFieldChange("certificateCode", val?.value)}
                        />
                    )}
                </DialogContent>

                <DialogActions>
                    <Button onClick={handleSavePayment} variant="contained" startIcon={<SaveIcon />}>
                        {t("common.save")}
                    </Button>
                    <Button onClick={() => setEditingRow(null)} variant="outlined" startIcon={<CancelIcon />}>
                        {t("common.cancel")}
                    </Button>
                </DialogActions>
            </Dialog>

            <PayByLink
                open={paybyLinkModal}
                onClose={() => setPaybyLinkModal(false)}
                setPayByLink={setPaybyLinkModal}
                xMobileNo={xMobileNo}
                documentNo={documentNo}
                setPaymentsOnDocument={wrappedSetPayments}
                initialPayments={initialPayments}
            />
        </Box>
    );
};

export default PaymentLines;
